import api from '@/helpers/api_helper';
import qs from 'qs';
import { apiUrls } from '@/constant/configure';
import {
    defaultEndMonth,
    defaultStartMonth,
    monthDefaultEnd,
    monthDefaultStart,
} from '@/pages/mall/Statement/SalesList/SalesListFilters';
import moment from 'moment-timezone';

// 소도몰 매출 통계 관련 타입
export interface MallProductListItem {
    productGroupId: string;
    productName: string;
}

export interface MallOptionStatisticsItem {
    optionName: string;
    matchingProductId: string;
    paidOrderCount: number;
    paidQuantity: number;
    paidAmount: number;
    cancelledOrderCount: number;
    cancelledQuantity: number;
    cancelledAmount: number;
    orderUnit?: number | null; // 발주단위 (mall_product.order_unit)
}

export interface MallProductStatisticsItem {
    productGroupId: string;
    productName: string;
    productImage: string | null;
    storeCount: number;
    totalPaidOrderCount: number;
    orderUnit?: number | null; // 발주단위 (mall_product.order_unit, 대표상품의 발주단위)
    totalPaidQuantity: number;
    totalPaidAmount: number;
    totalCancelledOrderCount: number;
    totalCancelledQuantity: number;
    totalCancelledAmount: number;
    hasOptions: boolean; // 옵션 존재 여부 (화살표 아이콘 표시용)
    options: MallOptionStatisticsItem[]; // 클릭 시 별도 API로 조회
}

export interface MallProductStatisticsResponse {
    items: MallProductStatisticsItem[];
    total: number;
    summary: {
        totalPaidOrderCount: number;
        totalPaidQuantity: number;
        totalPaidAmount: number;
        totalCancelledOrderCount: number;
        totalCancelledQuantity: number;
        totalCancelledAmount: number;
    };
}

export interface MallSalesStatisticsFilter {
    startDate?: string;
    endDate?: string;
    productName?: string;
    offset?: number;
    limit?: number;
}

export const getSelectableContractList = async (userId: string) => {
    try {
        return await api.get(`/seller/${userId}/contract/tiny`, { baseURL: apiUrls.matching }).then((r) => {
            return r.data?.items || [];
        });
    } catch (error) {
        throw error;
    }
};

export interface SalesBasicDataType {
    totalSales: number;
    salesProductCount: number;
    paymentCount: number;
    orderUnit?: number | null; // 발주단위 (mall_product.order_unit)
    paymentProductCount: number;
    paymentAmount: number;
    pointAmount: number;
    paymentAmountAverage: number;
    shippingPrice: number;
    refundCount: number;
    refundProductCount: number;
    refundAmount: number;
    refundRate: number;
    refundPoint: number;
}

export interface SalesDataByContractQuery {
    offset: number;
    limit: number;
    contractId?: string;
}
export interface SalesDataByContractListType extends SalesBasicDataType {
    productName: string;
}
export interface SalesDataByContractType {
    list: SalesDataByContractListType[];
    total: number;
}
export const getSalesDataByContract = async (
    mallId: string,
    query: SalesDataByContractQuery,
): Promise<SalesDataByContractType> => {
    try {
        const { limit = 15, offset = 0, contractId = '' } = query;
        const queryString = `contractId=${contractId}&offset=${offset}&limit=${limit}`;
        return await api.get(`/mall/${mallId}/sales/contract?${queryString}`).then((r) => {
            return { list: r.data?.items || [], total: r.data?.totalCount || 0 };
        });
    } catch (error) {
        throw error;
    }
};

export interface SalesDataByDateQuery {
    offset: number;
    limit: number;
    period: 'DAY' | 'MONTH';
    startDate: string;
    endDate: string;
    ownerType: 'VENDOR' | 'SELLER' | 'whole';
    startMonth: string;
    endMonth: string;
}
export interface SalesDataByDateListType extends SalesBasicDataType {
    date: string;
}
export interface SalesDataByDateType {
    list: SalesDataByDateListType[];
    total: number;
}
export const getSalesDataByDate = async (mallId: string, query: SalesDataByDateQuery): Promise<SalesDataByDateType> => {
    try {
        const {
            endDate = monthDefaultEnd,
            limit = 15,
            offset = 0,
            period = 'DAY',
            startDate = monthDefaultStart,
            ownerType = 'whole',
            startMonth = defaultEndMonth,
            endMonth = defaultStartMonth,
        } = query;
        const setOwnerType = ownerType === 'whole' ? '' : ownerType;
        const startValue =
            period === 'DAY' ? startDate : new Date(moment(startMonth).format('YYYY-MM-DDTHH:mm:ssZ')).toISOString();
        const endValue =
            period === 'DAY'
                ? endDate
                : new Date(
                      moment(endMonth).add(1, 'months').subtract(1, 'milliseconds').format('YYYY-MM-DDTHH:mm:ss.999Z'),
                  ).toISOString();
        const queryString = `endDate=${endValue}&period=${period}&startDate=${startValue}&limit=${limit}&offset=${offset}&ownerType=${setOwnerType}`;
        return await api.get(`/mall/${mallId}/sales/date?${queryString}`).then((r) => {
            return { list: r.data?.items || [], total: r.data?.totalCount || 0 };
        });
    } catch (error) {
        throw error;
    }
};

// =====================================
// 매장관리자용 소도몰 매출 통계 API
// =====================================

/**
 * 매장 상품 목록 조회 (필터용)
 */
export const getMallProductList = async (mallId: string): Promise<MallProductListItem[]> => {
    try {
        return await api
            .get(`/mall/${mallId}/sodomall/sales-statistics/products`, { baseURL: apiUrls.matching })
            .then((r) => r.data || []);
    } catch (error) {
        throw error;
    }
};

/**
 * 매장 상품별 매출 통계 조회
 */
export const getMallProductStatistics = async (
    mallId: string,
    filter: MallSalesStatisticsFilter,
): Promise<MallProductStatisticsResponse> => {
    try {
        const { startDate, endDate, productName, offset = 0, limit = 15 } = filter;
        const params = new URLSearchParams();
        params.append('offset', String(offset));
        params.append('limit', String(limit));
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (productName) params.append('productName', productName);

        return await api
            .get(`/mall/${mallId}/sodomall/sales-statistics/by-product?${params.toString()}`, {
                baseURL: apiUrls.matching,
            })
            .then(
                (r) =>
                    r.data || {
                        items: [],
                        total: 0,
                        summary: {
                            totalPaidOrderCount: 0,
                            totalPaidQuantity: 0,
                            totalPaidAmount: 0,
                            totalCancelledOrderCount: 0,
                            totalCancelledQuantity: 0,
                            totalCancelledAmount: 0,
                        },
                    },
            );
    } catch (error) {
        throw error;
    }
};

/**
 * 매장 상품 옵션별 통계 조회 (클릭 시 상세 조회)
 */
export const getMallProductOptionStatistics = async (
    mallId: string,
    productGroupId: string,
    startDate?: string,
    endDate?: string,
): Promise<MallOptionStatisticsItem[]> => {
    try {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        return await api
            .get(
                `/mall/${mallId}/sodomall/sales-statistics/by-product/${productGroupId}/options?${params.toString()}`,
                { baseURL: apiUrls.matching },
            )
            .then((r) => r.data || []);
    } catch (error) {
        throw error;
    }
};
